import { Controller } from "@hotwired/stimulus"

// Handles direct S3 uploads (pre-signed POST) using native XHR.
// Replaces blueimp-file-upload.
//
// Required data attributes on the controller element (the <form>):
//   data-s3-upload-url-value       — S3 bucket URL
//   data-s3-upload-fields-value    — JSON object of pre-signed POST fields
//   data-s3-upload-field-name-value — name attribute for the hidden key field submitted with the form
//
// Targets:
//   fileInput   — the <input type="file"> (required)
//   progressBar — the progress bar element (required)
//   submit      — the submit button (optional, disabled during upload)
//   preview     — <img> element to update src after upload (optional)

export default class extends Controller {
  static targets = ["fileInput", "progressBar", "submit", "preview"]
  static values = {
    url: String,
    fields: Object,
    fieldName: String,
  }

  upload(event) {
    const file = event.target.files[0]
    if (!file) return

    const formData = new FormData()
    Object.entries(this.fieldsValue).forEach(([k, v]) => formData.append(k, v))
    formData.append("file", file)

    if (this.hasSubmitTarget) this.submitTarget.disabled = true
    this.progressBarTarget.textContent = "Loading..."
    this.progressBarTarget.style.width = "0%"
    this.progressBarTarget.classList.remove("progress-bar-danger")
    this.progressBarTarget.classList.add("active")

    const xhr = new XMLHttpRequest()
    xhr.open("POST", this.urlValue)
    xhr.responseType = "document"

    xhr.upload.addEventListener("progress", (e) => {
      if (e.lengthComputable) {
        const pct = Math.round((e.loaded / e.total) * 100)
        this.progressBarTarget.style.width = pct + "%"
      }
    })

    xhr.addEventListener("load", () => {
      if (this.hasSubmitTarget) this.submitTarget.disabled = false

      const key = xhr.responseXML?.querySelector("Key")?.textContent
      if (xhr.status >= 200 && xhr.status < 300 && key) {
        this._setHiddenField(key)
        if (this.hasPreviewTarget) {
          this.previewTarget.src = "//images1.mtggoldfish.com/" + key
        }
        this.progressBarTarget.textContent = "Upload complete."
        this.progressBarTarget.classList.remove("active")
      } else {
        this._fail()
      }
    })

    xhr.addEventListener("error", () => {
      if (this.hasSubmitTarget) this.submitTarget.disabled = false
      this._fail()
    })

    xhr.send(formData)
  }

  _setHiddenField(key) {
    let hidden = this.element.querySelector(".s3-upload-key-field")
    if (!hidden) {
      hidden = document.createElement("input")
      hidden.type = "hidden"
      hidden.name = this.fieldNameValue
      hidden.classList.add("s3-upload-key-field")
      this.element.appendChild(hidden)
    }
    hidden.value = key
  }

  _fail() {
    this.progressBarTarget.classList.add("progress-bar-danger")
    this.progressBarTarget.classList.remove("active")
    this.progressBarTarget.textContent = "Upload failed."
  }
}
